/**
 * 로케일 기반 날짜·숫자 표기.
 *
 * 화면 곳곳에 'ko-KR' / 'en-US' 가 하드코딩되어 있어
 * 같은 앱 안에서도 표기가 제각각이었다. 현재 언어를 따르도록 한 곳에 모은다.
 */

import i18n, { DEFAULT_LANGUAGE } from '../i18n/config'

interface FormatOptions {
  locale?: string
  timeZone?: string
}

function currentLocale(locale?: string): string {
  return locale || i18n.language || DEFAULT_LANGUAGE
}

function toDate(value?: string | number | Date | null): Date | null {
  if (value === null || value === undefined) {
    return null
  }
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** 날짜만 (예: 2026. 3. 10. / Mar 10, 2026) */
export function formatDate(
  value?: string | number | Date | null,
  options: FormatOptions = {}
): string {
  const date = toDate(value)
  if (!date) {
    return ''
  }

  return new Intl.DateTimeFormat(currentLocale(options.locale), {
    year: 'numeric',
    month: options.locale === 'en' ? 'short' : 'numeric',
    day: 'numeric',
    timeZone: options.timeZone,
  }).format(date)
}

/** 날짜 + 시각 */
export function formatDateTime(
  value?: string | number | Date | null,
  options: FormatOptions = {}
): string {
  const date = toDate(value)
  if (!date) {
    return ''
  }

  return new Intl.DateTimeFormat(currentLocale(options.locale), {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: options.timeZone,
  }).format(date)
}

/** 천 단위 구분 기호 */
export function formatNumber(value: number, locale?: string): string {
  const safe = Number.isFinite(value) ? value : 0
  return new Intl.NumberFormat(currentLocale(locale)).format(safe)
}

/** 통화 표기 */
export function formatCurrency(
  value: number,
  currency = 'KRW',
  locale?: string
): string {
  const safe = Number.isFinite(value) ? value : 0
  return new Intl.NumberFormat(currentLocale(locale), {
    style: 'currency',
    currency,
    // 원화는 소수점을 쓰지 않는다
    maximumFractionDigits: currency === 'KRW' ? 0 : 2,
  }).format(safe)
}

export default { formatDate, formatDateTime, formatNumber, formatCurrency }
